export const SNOOKER_COLORS = { Yellow: 2, Green: 3, Brown: 4, Blue: 5, Pink: 6, Black: 7 };

// Table corrections are not pots: keep scores, breaks, turn and clocks intact.
export const snookerRedsSnapshot = (state) => Object.fromEntries([
    'redsRemaining', 'lastRedPotted', 'firstColorAfterLastRedPotted',
    'finalColorsPhase', 'freeBallActive',
].map(key => [key, state[key]]));

export const canAdjustSnookerReds = (state, delta) => {
    const reds = state.redsRemaining;
    if (state.isGameFinished || state.frameOver || ![-1, 1].includes(delta)
        || !Number.isInteger(reds) || reds < 0 || reds > 15
        || reds + delta < 0 || reds + delta > 15) return false;
    // Restoring reds must not silently restore already-scored clearance colours.
    return !(delta > 0 && reds === 0 && Object.values(state.colorsPottedFirstTime || {}).some(Boolean));
};

export const adjustSnookerReds = (state, delta) => {
    if (!canAdjustSnookerReds(state, delta)) return state;
    const redsRemaining = state.redsRemaining + delta;
    const crossesPhase = redsRemaining === 0 || state.redsRemaining === 0;
    return {
        ...state,
        redsRemaining,
        // A red removed without a legal pot earns no colour-after-red stroke.
        lastRedPotted: redsRemaining === 0,
        firstColorAfterLastRedPotted: redsRemaining === 0,
        finalColorsPhase: redsRemaining === 0,
        freeBallActive: crossesPhase ? false : state.freeBallActive,
    };
};

// The referee decides whether a free ball is awarded. This is the ball it
// replaces, not the physical colour nominated by the striker.
export const freeBallTarget = (state) => state.redsRemaining > 0
    ? { name: 'Red', points: 1 }
    : Object.entries(SNOOKER_COLORS)
        .filter(([name]) => !state.colorsPottedFirstTime?.[name])
        .map(([name, points]) => ({ name, points }))[0] || null;

export const minimumFoulPoints = (state) => Math.max(
    4, state.finalColorsPhase ? (freeBallTarget(state)?.points || 4) : 4
);

export const snookerScoringSnapshot = (state) => Object.fromEntries([
    'player1Score', 'player2Score', 'player1Break', 'player2Break', 'currentPlayer',
    'currentBreakPoints', 'currentBreakPottedBalls', 'player1PottedBalls', 'player2PottedBalls',
    'redsRemaining', 'colorsPottedFirstTime', 'lastRedPotted',
    'firstColorAfterLastRedPotted', 'finalColorsPhase', 'freeBallActive',
    'selectingFoulValue', 'timerOwner', 'shotRemaining', 'shotTimerIsRunning',
    'shotStartTimestamp', 'currentShotLimitMs', 'switchCount',
].map(key => [key, state[key]]));

export const potSnookerBall = (state, ball, { both = false } = {}) => {
    if (state.isGameFinished || state.frameOver || state.selectingFoulValue) return state;
    const target = state.freeBallActive ? freeBallTarget(state) : null;
    const scoredBall = target?.name || ball;
    const points = target ? target.points * (both && target.name === 'Red' ? 2 : 1)
        : (ball === 'Red' ? 1 : SNOOKER_COLORS[ball]);
    if (!points || (state.freeBallActive && !target)) return state;
    if (!target && ball === 'Red' && state.redsRemaining <= 0) return state;
    if (!target && state.finalColorsPhase && state.colorsPottedFirstTime[ball]) return state;

    const player = state.currentPlayer;
    const balls = both && target?.name === 'Red' ? ['Red', 'Red'] : [scoredBall];
    const next = {
        ...state,
        [`player${player}Score`]: state[`player${player}Score`] + points,
        [`player${player}Break`]: state[`player${player}Break`] + points,
        [`player${player}PottedBalls`]: [...state[`player${player}PottedBalls`], ...balls],
        currentBreakPoints: state.currentBreakPoints + points,
        currentBreakPottedBalls: [...state.currentBreakPottedBalls, ...balls],
        freeBallActive: false,
    };

    // A free ball is re-spotted. Only remove a real ball if it was also potted.
    if (!target || both) {
        if (scoredBall === 'Red') {
            next.redsRemaining = state.redsRemaining - 1;
            next.lastRedPotted = next.redsRemaining === 0;
        } else if (!target && state.lastRedPotted && !state.firstColorAfterLastRedPotted) {
            next.firstColorAfterLastRedPotted = true;
            next.finalColorsPhase = true;
        } else if (state.finalColorsPhase || target) {
            next.colorsPottedFirstTime = { ...state.colorsPottedFirstTime, [scoredBall]: true };
        }
    }
    return next;
};

export const applySnookerFoul = (state, points, now = Date.now()) => {
    if (state.isGameFinished || state.frameOver || !state.selectingFoulValue
        || !Number.isInteger(points) || points < minimumFoulPoints(state) || points > 7) return state;
    const recipient = state.currentPlayer === 1 ? 2 : 1;
    const elapsed = state.timerPausedDuration + (state.timerIsRunning && state.timerStartTimestamp
        ? now - state.timerStartTimestamp : 0);
    const shotLimit = elapsed < 300000 ? 15000 : 10000;
    return {
        ...state,
        [`player${recipient}Score`]: state[`player${recipient}Score`] + points,
        player1Break: 0,
        player2Break: 0,
        currentBreakPoints: 0,
        currentBreakPottedBalls: [],
        currentPlayer: recipient,
        selectingFoulValue: false,
        freeBallActive: false,
        firstColorAfterLastRedPotted: state.lastRedPotted || state.firstColorAfterLastRedPotted,
        finalColorsPhase: state.lastRedPotted || state.finalColorsPhase,
        timerOwner: state.timerSyncEnabled ? recipient : state.timerOwner,
        shotTimerIsRunning: false,
        shotStartTimestamp: null,
        shotRemaining: shotLimit,
        currentShotLimitMs: shotLimit,
        switchCount: (state.switchCount || 0) + 1,
    };
};

export const snookerPointsRemaining = (state) => {
    const colourDue = !state.finalColorsPhase
        && state.currentBreakPottedBalls?.slice(-1)[0] === 'Red';
    let points = state.finalColorsPhase
        ? Object.entries(SNOOKER_COLORS).reduce((sum, [ball, value]) => sum + (state.colorsPottedFirstTime[ball] ? 0 : value), 0)
        : state.redsRemaining * 8 + 27 + (colourDue ? 7 : 0);
    const target = state.freeBallActive ? freeBallTarget(state) : null;
    if (target) points += target.name === 'Red' ? 8 : target.points;
    return points;
};
